// ResidentLegend.js
// -------------------------------------------------------------------------
// A thin, reusable wrapper that mounts the proven /lab 3D model + lip-sync +
// voice pipeline into an arbitrary container on the learning surfaces
// (onboarding, hub, lessons, stage) WITHOUT forking or modifying any of the
// audio / A2F / ElevenLabs internals.
//
// It reuses ModelViewer exactly as /lab does:
//   - seed the model selection so ModelManager loads EXACTLY the matched
//     faculty (localStorage `selectedModelId` is ModelManager's highest
//     priority and is treated as a "known" selection, so the cold-visitor
//     guard never nulls it),
//   - `new ModelViewer(canvas)` then `await viewer.initialize()` (initialize
//     is what starts the animate() loop and wires the GreetingService to the
//     freshly loaded model — the constructor alone does NOT animate),
//   - speak through `greetingService.playPersonalizedGreeting(...)`, the same
//     public entry /lab uses, which routes the mp3 through the A2F gate and
//     NEVER pauses/suspends the speaker.
//
// SACRED: do not modify the audio/A2F/ElevenLabs pipeline. This file only
// composes the existing modules.
// -------------------------------------------------------------------------

import { ModelViewer } from "./ModelViewer.js";
import * as characterData from "./characterData.js";

export class ResidentLegend {
    constructor(opts = {}) {
        this.canvas = null;
        this.viewer = null;
        this.characterId = null;
        this._ready = false;
        this._loadPromise = null;
        this._readyCbs = [];
        this.bodyLiveClass = opts.bodyLiveClass || "legend-live";
        // Task #394 — optional full-body fit knob forwarded to ModelViewer
        // ({ heightMargin, widthMargin, anchor }). Only the /learn lesson
        // player passes it (stage entry); every other resident surface
        // leaves it unset and keeps the classic Task #284 fit.
        this.fullBodyFit = opts.fullBodyFit || null;
        // Explicitly opt a surface into the posed-silhouette, eye-level lesson
        // composition. Merely mounting ResidentLegend never changes /lab or
        // other resident surfaces.
        this.framingPolicy = opts.framingPolicy || null;
        // Surface-owned look, independent of the Lab's saved lighting choice.
        // Unspecified keeps the viewer baseline for other resident embeds.
        this.lightingMode = opts.lightingMode || null;

        if (opts.canvas) this.mount(opts.canvas);
    }

    /** Attach to a <canvas> element (or a selector resolving to one). */
    mount(canvas) {
        this.canvas =
            typeof canvas === "string"
                ? document.querySelector(canvas)
                : canvas;
        return this;
    }

    isReady() {
        return this._ready;
    }

    /** Run cb once the 3D model is fully loaded (fires immediately if ready). */
    onReady(cb) {
        if (typeof cb !== "function") return;
        if (this._ready) {
            try {
                cb(this);
            } catch (_) {}
        } else {
            this._readyCbs.push(cb);
        }
    }

    /**
     * Load EXACTLY the matched faculty by its character UUID. Idempotent —
     * repeat calls return the in-flight / resolved load promise so the heavy
     * 3D load can be kicked off as a "load cover" and awaited later.
     */
    async load(characterId) {
        if (this._loadPromise) return this._loadPromise;
        this._loadPromise = this._doLoad(characterId);
        return this._loadPromise;
    }

    async _doLoad(characterId) {
        if (!this.canvas) {
            throw new Error("ResidentLegend: call mount(canvas) before load()");
        }
        if (!characterId) {
            throw new Error("ResidentLegend: load() needs a character id");
        }
        this.characterId = characterId;

        // Seed the selection so ModelManager loads this exact faculty. Source
        // "localStorage" is treated as a known selection (cold-visitor guard
        // skipped); ModelManager auto-clears the key after capturing it.
        try {
            localStorage.setItem("selectedModelId", characterId);
        } catch (_) {}
        // Belt-and-suspenders for the "known selection" guard.
        window.PRESELECTED_MODEL_ID = characterId;

        const viewer = new ModelViewer(this.canvas);
        this.viewer = viewer;
        // Start the existing lazy HDRI load alongside the model download.
        // Do not persist a surface default or add a gate to lesson startup.
        if (this.lightingMode) viewer.setLightingMode(this.lightingMode, false);
        // Task #262 — learning surfaces drive ALL speech explicitly (lesson
        // narration / speakGreeting), so the /lab model-load AUTO greeting — and,
        // on Safari, its "read their first words" permission card — must never
        // fire here. Neutralize ONLY this resident instance's static-greeting
        // entry (playGreeting → playPersonalizedGreeting → the Safari popup); the
        // /lab GreetingService CLASS and the Lab's own instance are untouched, and
        // personalized narration (playPersonalizedGreeting) still works. Set
        // BEFORE initialize() registers the spawn-complete / Safari-fallback
        // greeting, so it is always in place before the greeting can fire.
        try {
            const gs = viewer.greetingService;
            if (gs && typeof gs.playGreeting === "function") {
                gs.playGreeting = async () => {};
            }
        } catch (_) {}
        // Task #251 — hard-pin the model to THIS faculty's UUID. ModelManager
        // runs twice (ctor + initialize) and the second pass can fall back to
        // the process-global CURRENT_MODEL (the last /lab pick), loading the
        // wrong teacher. forcedModelId makes initialize() ignore that global on
        // the resident path; /lab never sets it, so the Lab is untouched.
        viewer.forcedModelId = characterId;
        // Task #251 — render the legend into the small bottom-left corner box
        // (the canvas is CSS-sized), not the full window. Opt-in; /lab untouched.
        viewer.useCanvasSize = true;
        // Task #284 — frame the teacher FULL-BODY (head to feet) in that box
        // instead of the /lab portrait crop. Opt-in; /lab untouched.
        viewer.fullBodyFraming = true;
        // Task #394 — optional per-surface fit tuning (see ctor note).
        if (this.fullBodyFit || this.framingPolicy) {
            viewer.fullBodyFit = {
                ...(this.fullBodyFit || {}),
                ...(this.framingPolicy
                    ? { policy: this.framingPolicy }
                    : {}),
            };
        }
        // Expose for any inline lookups that mirror /lab; harmless on /tutor.
        if (!window.modelViewer) window.modelViewer = viewer;

        // initialize() fetches /api/models, loads the selected model, wires the
        // GreetingService to it, and starts the animate() loop.
        await viewer.initialize();

        // The corner portrait sizes the renderer to the canvas box, not the
        // window. initialize() only sized once (window-based) during the ctor,
        // so re-fit now that useCanvasSize is on and the model/frame exist.
        try {
            viewer.configureRendererSize();
            window.dispatchEvent(new Event("resize"));
        } catch (_) {}

        this._ready = true;
        const cbs = this._readyCbs.slice();
        this._readyCbs.length = 0;
        cbs.forEach((cb) => {
            try {
                cb(this);
            } catch (_) {}
        });
        return viewer;
    }

    /** Look up the character record from the shared character data. */
    getCharacterRecord(characterId) {
        const id = characterId || this.characterId;
        if (!id) return null;
        try {
            // characterData.js exports `const characterData = { ...records,
            // getCharacterById() }`, so the records + lookup live on the inner
            // object, not the module namespace.
            const data = characterData.characterData || null;
            if (data) {
                if (typeof data.getCharacterById === "function") {
                    return data.getCharacterById(id) || null;
                }
                return data[id] || null;
            }
        } catch (_) {}
        return null;
    }

    /**
     * Speak a personalized greeting in the legend's own voice with lip-sync.
     * Delegates to the exact public entry /lab uses; the speaker is never
     * paused. `greetingData` is { greeting_text, audio_base64, timing_data }.
     * Returns true if the spoken path was taken, false otherwise (so callers
     * can fall back to a plain audio element without ever double-playing).
     */
    async speakGreeting(greetingData, record) {
        if (!this.viewer || !this._ready) return false;
        const gs = this.viewer.greetingService;
        if (!gs || typeof gs.playPersonalizedGreeting !== "function")
            return false;
        if (!greetingData || !greetingData.audio_base64) return false;
        const rec = record || this.getCharacterRecord() || {};
        try {
            await gs.playPersonalizedGreeting(greetingData, rec);
            return true;
        } catch (e) {
            console.warn("[ResidentLegend] speakGreeting failed:", e);
            return false;
        }
    }

    /**
     * Set the coarse conversational state: "idle" | "listen" | "speak".
     * No-op until the model (and its AnimationController) are ready. During an
     * active greeting/turn the pipeline already drives the speak state, so this
     * is mainly for idle/listen on the learning surfaces.
     */
    setState(state) {
        const ac = this.viewer && this.viewer.animationController;
        if (!ac || typeof ac.play !== "function") return;
        try {
            ac.play(state);
        } catch (_) {}
    }

    /** Show / hide the resident stage via a body class the page styles. */
    show() {
        try {
            document.body.classList.add(this.bodyLiveClass);
        } catch (_) {}
    }
    hide() {
        try {
            document.body.classList.remove(this.bodyLiveClass);
        } catch (_) {}
    }

    getViewer() {
        return this.viewer;
    }

    dispose() {
        try {
            this.viewer && this.viewer.dispose && this.viewer.dispose();
        } catch (_) {}
        this.viewer = null;
        this._ready = false;
        this._loadPromise = null;
        this.hide();
    }
}

export default ResidentLegend;
